import { Injectable } from '@angular/core';
import { WorkbookLog } from '../models/workbookLog';
import { IOfflineWorkbookService } from './iOfflineWorkbook.service';
import { Subject, Observable } from 'rxjs';
import { clear, createStore, del, get, set, type UseStore, values } from 'idb-keyval';

@Injectable({
  providedIn: 'root'
})
export class OfflineworkbookService implements IOfflineWorkbookService {
  _dbname = 'offlineworkbook_v4';
  _tableName = 'workbookqueue';
  _queuedItems = new Subject<number>();
  private readonly _initTimeoutMs = 3000;
  private readonly _store: UseStore;
  private _initPromise: Promise<boolean>;

  constructor() {
    this._store = createStore(this._dbname, this._tableName);
    this._initPromise = this.initDatabase();
  }

  initDatabase(): Promise<boolean> {
    console.info('[OfflineWorkbookService] initializing offline workbook database', { databaseName: this._dbname });
    return values(this._store).then(() => {
      console.info('[OfflineWorkbookService] offline workbook database initialized', { databaseName: this._dbname });
      return true;
    }).catch(error => {
      console.error('[OfflineWorkbookService] offline workbook database initialization failed', {
        databaseName: this._dbname,
        error
      });
      return false;
    });
  }

  private async ensureInitialized() {
    const initialized = await this.withTimeout(
      this._initPromise,
      this._initTimeoutMs,
      'Timed out initializing offline workbook database'
    );
    if (!initialized) {
      console.warn('[OfflineWorkbookService] offline workbook database unavailable', { databaseName: this._dbname });
      throw new Error('Could not initialize offline workbook database');
    }
  }

  private async withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
    let timeoutHandle: ReturnType<typeof setTimeout>;
    const timeout = new Promise<T>((_, reject) => {
      timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs);
    });

    return Promise.race([
      promise.finally(() => clearTimeout(timeoutHandle)),
      timeout
    ]);
  }

  private isMissingObjectStoreError(error: unknown): boolean {
    const message = (error as { message?: string })?.message || String(error || '');
    return message.indexOf('object stores was not found') > -1 || message.indexOf('object store not found') > -1;
  }

  private async resetDatabase() {
    try {
      await clear(this._store);
    } catch {
      // ignore reset errors and continue to re-init
    }
    this._initPromise = this.initDatabase();
    await this._initPromise;
  }

  private async runWithRecovery<T>(operation: () => Promise<T>): Promise<T> {
    await this.ensureInitialized();
    try {
      return await operation();
    } catch (error) {
      if (!this.isMissingObjectStoreError(error)) {
        throw error;
      }
      await this.resetDatabase();
      return operation();
    }
  }

  public deleteDatabase(): Promise<number> {
    return this.runWithRecovery(() => this.clearStore());
  }

  private async clearStore(): Promise<number> {
    const count = (await values(this._store)).length;
    await clear(this._store);
    return count;
  }

  private putEntry(entry: IWorkbookQueueEntry): Promise<void> {
    return set(entry.id, entry, this._store);
  }

  private deleteEntry(id: string): Promise<void> {
    return del(id, this._store);
  }

  private async getAllEntries(jobId: string | null): Promise<IWorkbookQueueEntry[]> {
    const entries = await values<IWorkbookQueueEntry>(this._store);
    return jobId === null ? entries : entries.filter(entry => entry.jobId === jobId);
  }

  public queueCreate(logentry: WorkbookLog): Promise<number | unknown[]> {
    return this.runWithRecovery(async () => {
      const entry = new WorkbookQueueEntry();
      entry.workbookLog = logentry;
      entry.jobId = logentry.jobId;
      entry.id = logentry.id;
      entry.operation = Operation.Insert;
      await this.putEntry(entry);
      this.updateQueuedItems();
      return 1;
    });
  }
  public async queueUpdate(logentry: WorkbookLog) {
    return this.runWithRecovery(async () => {
      const existing = await this.getByIdInternal(logentry.id);
      if (existing == null) {
        const entry = new WorkbookQueueEntry();
        entry.workbookLog = logentry;
        entry.jobId = logentry.jobId;
        entry.id = logentry.id;
        entry.operation = Operation.Update;
        await this.putEntry(entry);
        this.updateQueuedItems();
        return 1;
      } else {
        existing.workbookLog = logentry;
        await this.putEntry(existing);
        this.updateQueuedItems();
        return 1;
      }
    });
  }

  private async getByIdInternal(id: string): Promise<IWorkbookQueueEntry | null> {
    const result = await get<IWorkbookQueueEntry>(id, this._store);
    return result ?? null;
  }

  public async getById(id: string): Promise<IWorkbookQueueEntry | null> {
    try {
      return await this.runWithRecovery(() => this.getByIdInternal(id));
    } catch (error) {
      console.warn('[OfflineWorkbookService] offline workbook entry unavailable', { databaseName: this._dbname, id, error });
      return null;
    }
  }

  public async queueDelete(logentry: WorkbookLog) {
    await this.runWithRecovery(async () => {
      const existing = await this.getByIdInternal(logentry.id);
      if (existing == null) {
        const entry = new WorkbookQueueEntry();
        entry.workbookLog = logentry;
        entry.jobId = logentry.jobId;
        entry.id = logentry.id;
        entry.operation = Operation.Delete;
        await this.putEntry(entry);
        this.updateQueuedItems();
      } else if (existing.operation === Operation.Insert) {
        await this.deleteEntry(logentry.id);
        this.updateQueuedItems();
      } else if (existing.operation === Operation.Update) {
        existing.operation = Operation.Delete;
        await this.putEntry(existing);
        this.updateQueuedItems();
      }
    });
  }


  public async getQueueEntries(jobId: string | null): Promise<IWorkbookQueueEntry[]> {
    try {
      return await this.runWithRecovery(() => this.getAllEntries(jobId));
    } catch (error) {
      console.warn('[OfflineWorkbookService] offline workbook queue unavailable', { databaseName: this._dbname, jobId, error });
      return [];
    }
  }

  public async removeQueuedEntry(entry: IWorkbookQueueEntry) {
    await this.runWithRecovery(async () => {
      await this.deleteEntry(entry.id);
      this.updateQueuedItems();
    });
  }

  private updateQueuedItems() {
    this.getQueueEntries(null).then(result => {
      if (result) {
        this._queuedItems.next(result.length);
      }
    }).catch(() => {
      // cache may not be available in all environments; ignore.
    });
  }

  public queuedItemsObservable(): Observable<number> {
    return this._queuedItems;
  }

}
export class IWorkbookQueueEntry {
  id = '';
  jobId = '';
  workbookLog = new WorkbookLog();
  operation = Operation.Insert;
}
export class WorkbookQueueEntry implements IWorkbookQueueEntry {
  id = ''; // new guid;
  jobId = '';
  workbookLog = new WorkbookLog();
  operation = Operation.Insert;
}
export enum Operation {
  Insert = 'Insert',
  Update = 'Update',
  Delete = 'Delete',
}
